import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// --- Import assets ---
import image552 from "../assets/image552.png";
import image554 from "../assets/image554.png";
import image555 from "../assets/image555.png";
import image556 from "../assets/image556.png";
import image557 from "../assets/image557.png";
import image559 from "../assets/image559.png";
import image560 from "../assets/image560.png";
import image561 from "../assets/image561.png";
import image562 from "../assets/image562.png";
import image563 from "../assets/image563.png";
import image564 from "../assets/image564.png";

// Why Choose Medimind section images
import image539 from "../assets/image539.png";
import image540 from "../assets/image540.png";
import image541 from "../assets/image541.png";
import image542 from "../assets/image542.png";
import image543 from "../assets/image543.png";
import image544 from "../assets/image544.png";

// Compliance images
import image630 from "../assets/image630.png";
import image631 from "../assets/image631.png";
import image632 from "../assets/image632.png";
import image633 from "../assets/image633.png";
import image634 from "../assets/image634.png";
import image635 from "../assets/image635.png";

// Partner logos
import logo1 from "../assets/logo1.png";
import logo2 from "../assets/logo2.png";
import logo3 from "../assets/logo3.png";
import logo4 from "../assets/logo4.png";
import logo5 from "../assets/logo5.png";
import logo6 from "../assets/logo6.png";
import logo7 from "../assets/logo7.png";
import logo8 from "../assets/logo8.png";
import logo9 from "../assets/logo9.png";
import logo10 from "../assets/logo10.png";
import logo11 from "../assets/logo11.png";
import logo12 from "../assets/logo12.png";
import logo13 from "../assets/logo13.png";
import logo14 from "../assets/logo14.png";
import logo15 from "../assets/logo15.png";
import logo16 from "../assets/logo16.png";
import logo17 from "../assets/logo17.png";
import logo18 from "../assets/logo18.png";
import logo19 from "../assets/logo19.png";
import logo20 from "../assets/logo20.png";
import logo21 from "../assets/logo21.png";
import logo22 from "../assets/logo22.png";

const services = [
  {
    image: image563,
    title: "Certified Information Security",
    description:
      "Certified to the highest global standards, ensuring patient data is always secure, private, and protected.",
  },
  {
    image: image564,
    title: "Certified Quality Management",
    description:
      "Committed to quality and consistency, delivering reliable healthcare solutions with continuous improvement.",
  },
  {
    image: image552,
    title: "In patient & Out patient",
    description:
      "MedOps streamlines IP & OP workflows, ensuring seamless admissions, appointments, treatment tracking, and billing. AI-driven bed management, automated prescriptions, lab integration, and digital health records enhance efficiency. Reduce wait times, optimize resources, and deliver faster, smarter, and patient-friendly care with real-time analytics and automation.",
  },
  {
    image: image554,
    title: "Branch-wise and Central Pharmacy Management",
    description:
      "Seamlessly manage medication inventories across multiple branches and a centralized hub. MedOps ensures synchronized stock tracking, efficient distribution, and regulatory compliance, empowering healthcare systems with better control and reduced wastage.",
  },
  {
    image: image555,
    title: "Ward + OT Management",
    description:
      "Streamline ward and operation theatre workflows with MedOps’s intelligent scheduling, occupancy monitoring, and resource allocation features. Ensure timely surgeries, reduce patient wait times, and improve coordination among medical teams.",
  },
  {
    image: image556,
    title: "Voice-Based AI Prescription Generation",
    description:
      "MedOps's AI-powered voice prescription feature enables doctors to dictate prescriptions instantly, reducing manual effort. It ensures accuracy, faster consultations, and automatic patient record updates. Integrated with pharmacy and billing, it enhances efficiency, minimizes errors, and improves patient care with seamless digital prescriptions sent via WhatsApp.",
  },
  {
    image: image557,
    title: "Real-time inventory tracking",
    description:
      "MedOps ensures real-time inventory visibility across multiple branches, preventing stockouts and overstocking. Automated tracking, expiry alerts, and centralized pharmacy integration streamline supply management. With AI-driven demand forecasting, hospitals can optimize stock levels, reduce wastage, and ensure timely availability of critical medicines and supplies.",
  },
  {
    image: image559,
    title:
      "Discover how AI-powered hospital management systems like MedOps reduce paperwork, improve staff efficiency, and enable data-driven decisions",
    description:
      "MedOps effortlessly connects hospital and pharmacy operations, ensuring real-time prescription processing, automated billing, and inventory synchronization. Doctors, pharmacists, and admins collaborate seamlessly, reducing errors and enhancing efficiency. With AI-driven stock management and instant prescription fulfillment, patient care becomes faster, smarter, and hassle-free.",
  },
  {
    image: image560,
    title: "AI-Powered Diagnostic & Lab Integration",
    description:
      "MedOps automates diagnostic report generation with AI-driven accuracy. Seamlessly integrated with labs, doctors, and patient records, it ensures faster results, minimal errors, and real-time access. Reports are instantly analyzed, categorized, and shared, enabling quicker diagnosis, better treatment decisions, and enhanced patient care.",
  },
  {
    image: image561,
    title: "Advanced Analytics & Campaign Management",
    description:
      "MedOps offers AI-powered analytics to track patient trends, treatment outcomes, and operational efficiency. With predictive insights and real-time data, hospitals can optimize resources and enhance patient care. The campaign management module enables targeted outreach, automated follow-ups, and personalized health programs, improving patient engagement and retention.",
  },
  {
    image: image562,
    title: "Centralized Data Security",
    description:
      "Protect sensitive patient and operational data with robust, centralized security protocols. MedOps implements encryption, access control, and compliance-ready logging to ensure data integrity and confidentiality across all hospital departments.",
  },
];

// --- ServiceCard Component ---
const ServiceCard = ({ service }) => {
  const [expanded, setExpanded] = useState(false);
  const shortText = service.description.slice(0, 200) + "...";

  return (
    <div className="flex flex-col md:flex-row bg-white rounded-xl shadow-lg overflow-hidden w-full mx-auto h-auto md:h-[500px]">
      {/* Left: Image */}
      <div className="w-full md:w-1/2 h-64 md:h-[500px]">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right: Content */}
      <div className="p-6 md:p-8 flex flex-col items-start justify-center w-full md:w-1/2 h-auto md:h-[500px]">
        <h4 className="text-2xl md:text-3xl font-bold text-[#3d302c] mb-4">
          {service.title}
        </h4>
        <p className="text-[#3d302c] text-sm md:text-base leading-relaxed mb-4">
          {expanded ? service.description : shortText}
        </p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-[#3d302c] font-bold cursor-pointer hover:underline"
        >
          {expanded ? "Read Less" : "Read More"}
        </button>
      </div>
    </div>
  );
};

const Home = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1));
    setAutoScroll(false);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1));
    setAutoScroll(false);
  };

  useEffect(() => {
    if (!autoScroll) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1));
    }, 2000);
    return () => clearInterval(interval);
  }, [autoScroll]);

  return (
    <section className="bg-[#d2e5e4] min-h-screen flex flex-col items-center w-full overflow-x-hidden">
      {/* Hero */}
      <div className="flex flex-col items-center text-[#3d302c] max-w-7xl px-4 text-center">
        <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold mb-6 leading-tight mt-0">
          Revolutionizing Healthcare with AI powered HMS
        </h2>
        <p className="text-base md:text-xl font-medium mb-6 leading-relaxed">
          “Seamless, Paperless, Empowered. MedOps is a next-gen
          <span className="font-bold text-orange-400">
            {" "}
            Hospital Management System (HMS){" "}
          </span>
          that combines AI and digital workflows to automate tasks, simplify
          records, and enhance decisions—delivering smarter, faster, paperless
          care.”
        </p>
      </div>

      {/* Services Carousel */}
      <div
        className="relative w-full max-w-[1200px] flex items-center justify-center mt-6 px-2 sm:px-4 overflow-hidden"
        onMouseEnter={() => setAutoScroll(false)}
        onMouseLeave={() => setAutoScroll(true)}
      >
        {/* Left Arrow */}
        <button
          onClick={handlePrev}
          className="absolute left-0 z-10 p-2 sm:p-3 bg-[#3d302c] text-white rounded-full shadow-md hover:bg-[#5a4742] transition"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Active Card */}
        <div className="relative w-full h-[500px] flex items-center justify-center">
          <AnimatePresence mode="sync">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
            >
              <ServiceCard service={services[activeIndex]} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right Arrow */}
        <button
          onClick={handleNext}
          className="absolute right-0 z-10 p-2 sm:p-3 bg-[#3d302c] text-white rounded-full shadow-md hover:bg-[#5a4742] transition"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Why Choose Section */}
      <section className="bg-[#d2e5e4] w-full py-20 px-4 text-[#3d302c] overflow-x-hidden">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: false, amount: 0.3 }}
          className="text-3xl sm:text-4xl font-bold text-center mb-12"
        >
          Why Choose Medimind?
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-7xl mx-auto w-full">
          {[
            {
              title: "Smarter Care with AI:",
              desc: "Harness the power of artificial intelligence to make every clinical decision sharper and more accurate. From triage to diagnostics, MediMind ensures doctors and care teams get real-time support that reduces errors and improves patient outcomes.",
              img: image539,
            },
            {
              title: "Plug & Play Integration:",
              desc: "No disruption, no complexity — MediMind fits seamlessly into your existing workflows. It integrates with EMR, ABHA/ABDM systems, diagnostic devices, and pharmacy modules so your ecosystem runs smoothly from day one.",
              img: image540,
            },
            {
              title: "Complete Healthcare Suite:",
              desc: "One unified platform for everything: consultations, prescriptions, diagnostics, billing, and digital health records. Say goodbye to fragmented systems and empower your team with a 360° view of every patient’s journey.",
              img: image541,
            },
            {
              title: "Built to Scale:",
              desc: "Whether you run a single clinic, a multi-specialty hospital, or a multi-chain network, MediMind grows with you. Its cloud-ready architecture ensures performance, security, and scalability without limits.",
              img: image542,
            },
            {
              title: "Insights That Matter:",
              desc: "Transform raw data into actionable intelligence. MediMind’s real-time dashboards and predictive analytics help you spot trends, optimize resources, and make faster, evidence-driven decisions.",
              img: image543,
            },
            {
              title: "Patient-First Always:",
              desc: "MediMind puts patients at the heart of care with digital records, reminders, teleconsultations, and personalized health journeys. Better engagement means healthier outcomes and long-lasting trust.",
              img: image544,
            },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              viewport={{ once: false, amount: 0.3 }}
              className="bg-white text-black px-5 py-8 rounded-xl shadow-md flex flex-col items-start gap-3 w-full"
            >
              <img
                src={feature.img}
                alt={feature.title}
                className="h-16 w-auto"
              />
              <h4 className="font-semibold text-base sm:text-lg">
                {feature.title}
              </h4>
              <p className="text-sm text-gray-700">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Compliance */}
      <section className="bg-white px-6 py-16 font-sans w-full overflow-x-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-2xl md:text-3xl font-bold mb-10 text-[#3d302c]"
          >
            Compliance
          </motion.h2>

          <motion.div
            className="mt-6 w-full overflow-x-auto pb-6 px-4 no-scrollbar"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex gap-6 w-fit">
              {[
                { title: "DICOM", alt: "DICOM Logo", image: image630 },
                {
                  title: "ISO27001-2022",
                  alt: "SNOMED CT Logo",
                  image: image631,
                },
                { title: "ISO 9001:2015", alt: "HL7 Logo", image: image632 },
                { title: "ICD10", alt: "ICD10 Logo", image: image633 },
                { title: "HL7", alt: "ICD10 Logo", image: image634 },
                { title: "SNOMEDCT", alt: "ICD10 Logo", image: image635 },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  className="relative flex-shrink-0 w-64 h-40 flex flex-col items-center justify-center 
                     bg-white border border-gray-300 rounded-lg hover:shadow-lg transition p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    ease: "easeOut",
                    delay: idx * 0.1,
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="w-24 h-16 object-contain"
                  />
                  <p className="mt-4 text-lg font-medium text-[#3d302c]">
                    {item.title}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Evidences */}
      <section className="bg-[#d2e5e4] w-full py-20 px-4 text-[#3d302c] overflow-x-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: false }}
          >
            Our Evidence
          </motion.h2>

          <motion.p
            className="text-base sm:text-lg font-medium mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: false }}
          >
            Medimind delivers proven results, making it the trusted choice for
            simplifying and enhancing everyone's work.
          </motion.p>

          <motion.div
            className="bg-white text-[#3d302c] rounded-xl px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-5xl mx-auto shadow-md"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
            viewport={{ once: false }}
          >
            <div className="text-left">
              <h3 className="text-4xl font-bold mb-2">96%</h3>
              <p className="text-sm font-medium">
                Manage multiple hospitals & pharmacies from a single platform.
              </p>
            </div>

            <div className="text-left">
              <h3 className="text-4xl font-bold mb-2">90%</h3>
              <p className="text-sm font-medium">
                AI-Driven Diagnostic Reports Automated
              </p>
            </div>

            <div className="text-left">
              <h3 className="text-4xl font-bold mb-2">96%</h3>
              <p className="text-sm font-medium">
                Campaign & Patient Management done
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#d2e5e4] w-full py-20 px-4 text-[#3d302c] overflow-x-hidden">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            className="text-3xl sm:text-4xl font-bold mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: false }}
          >
            What Hospitals Are Saying?
          </motion.h2>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                text: `MedOps has transformed our hospital operations. The voice-based prescription generation is a game-changer for doctors, saving us valuable time while ensuring accuracy. Managing IP, OP, pharmacy, and billing has never been easier!`,
                author: "— Mr. Chakrapani, VMT Hospitals",
              },
              {
                text: `With MedOps's central pharmacy and inventory management, we no longer worry about stock shortages or expired medicines. The AI-powered diagnostic reports provide instant insights, allowing us to deliver faster and better patient care.`,
                author:
                  "— Mr. Ravikumar, Senior Physician, Lavanya Multi Speciality",
              },
              {
                text: `The multi-branch hospital management system has been a blessing! We can now oversee multiple facilities from a single dashboard, improving efficiency and decision-making. The automated billing and insurance claim tracking have streamlined our finances like never before.`,
                author:
                  "— Mr. Ajay Varma, Hospital Administrator, Srinivasa Hospitals",
              },
              {
                text: `With MedOps, our central pharmacy distribution is completely automated. Stock tracking across multiple branches is seamless, and the auto-replenishment feature ensures we never run out of critical medicines.`,
                author:
                  "— Mr. Rama Krishna, Pharmacy Manager, Central MedPharm",
              },
              {
                text: `The doctor mobile app lets me manage patient history, prescriptions, and appointments on the go. It's intuitive, fast, and keeps all my data in one place. Truly a next-gen healthcare platform!`,
                author: "— Dr. Trilok Rao, Yashodhara Hospitals",
              },
              {
                text: `At Westin Hospital, we've integrated the doctor app into our daily workflow. From tracking cases to seamless patient communication, it's become indispensable. It’s not just an app—it’s my digital assistant in scrubs!`,
                author: "— Dr. Mukund, westin Hospital",
              },
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                className="bg-white p-6 rounded-xl shadow-md border border-[#ddd] transition-transform duration-300 hover:scale-[1.02] text-[#3d302c]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: idx * 0.1,
                }}
                viewport={{ once: false }}
              >
                <p className="text-[#5a4c48] text-sm italic mb-4">
                  “{testimonial.text}”
                </p>
                <h4 className="font-semibold text-right">
                  {testimonial.author}
                </h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="w-full bg-white py-16 px-6 text-center overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.p
            className="text-gray-700 text-xl font-medium mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: false }}
          >
            Trusted by companies of all sizes around the world
          </motion.p>

          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: false }}
          >
            {[
              logo1,
              logo16,
              logo5,
              logo18,
              logo19,
              logo4,
              logo2,
              logo20,
              logo21,
              logo17,
              logo10,
              logo9,
              logo15,
              logo13,
              logo12,
              logo22,
              logo6,
              logo3,
              logo7,
              logo8,
              logo11,
              logo14,
            ].map((logo, index) => (
              <div
                key={index}
                className="relative group h-24 w-full overflow-hidden rounded-md shadow-sm"
              >
                <img
                  src={logo}
                  alt={`Partner ${index + 1}`}
                  className="w-full h-full object-contain p-2 transition-opacity duration-300 group-hover:opacity-40"
                />
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </section>
  );
};

export default Home;
